import { cn } from "../../services/Utils";
import { AnimatePresence, motion } from "motion/react";
import { useState, useEffect, useRef } from "react";
import { APIs } from "../../services/Apis";

const DURATION_MS = 4000;

const LoaderSpinner = () => (
  <div className="flex flex-col items-center justify-center gap-4">
    <div className="relative w-12 h-12">
      <div className="absolute inset-0 rounded-full border-4 border-neutral-200"></div>
      <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary animate-spin"></div>
    </div>
    <p className="text-sm text-neutral-500">Preparing your experience...</p>
  </div>
);

const CheckFilled = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={cn("w-6 h-6", className)}
  >
    <path
      fillRule="evenodd"
      d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
      clipRule="evenodd"
    />
  </svg>
);

const CheckIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={cn("w-6 h-6", className)}
  >
    <path d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

export const MultiStepLoaderNodeSettings = ({
  loading,
  actionId,
}) => {
  const [messages, setMessages] = useState([]);
  const [isFetchingMessages, setIsFetchingMessages] = useState(true);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!loading || !actionId || hasFetched.current) return;

    const fetchMessages = async () => {
      try {
        hasFetched.current = true;
        const response = await APIs.getSegmentActionDetailsWaitingMessages(actionId);

        if (response && response.success && response.messages) {
          setMessages(response.messages);
        } else {
          // Fallback messages if API fails
          setMessages([
            "Loading action details...",
            "Preparing settings...",
            "Almost ready...",
          ]);
        }
      } catch (error) {
        console.error("Failed to fetch waiting messages:", error);
        // Fallback messages
        setMessages([
          "Loading action details...",
          "Preparing settings...",
          "Almost ready...",
        ]);
      } finally {
        setIsFetchingMessages(false);
      }
    };

    fetchMessages();
  }, [loading, actionId]);

  useEffect(() => {
    if (!loading || isFetchingMessages || messages.length === 0) return;

    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % messages.length);
    }, DURATION_MS);

    return () => clearInterval(interval);
  }, [loading, isFetchingMessages, messages.length]);

  // Reset state when loading becomes false
  useEffect(() => {
    if (!loading) {
      setCurrentMessageIndex(0);
      setMessages([]);
      setIsFetchingMessages(true);
      hasFetched.current = false;
    }
  }, [loading]);

  return (
    <AnimatePresence mode="wait">
      {loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 z-50 flex items-center justify-center bg-white/95 backdrop-blur-sm"
        >
          <div className="w-full max-w-2xl mx-auto px-6">
            {isFetchingMessages ? (
              <LoaderSpinner />
            ) : (
              <div className="space-y-4">
                {messages.map((message, index) => {
                  const isActive = index === currentMessageIndex;
                  const isPast = index < currentMessageIndex;
                  const distance = Math.abs(index - currentMessageIndex);
                  const opacity = isActive ? 1 : Math.max(0.3 - distance * 0.1, 0.1);

                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{
                        opacity: opacity,
                        y: 0,
                        scale: isActive ? 1 : 0.98,
                      }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className={cn(
                        "flex items-start gap-3 p-4 rounded-xl transition-all duration-300",
                        isActive && "bg-primary/5 border border-primary/20"
                      )}
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {isPast ? (
                          <CheckFilled className="text-primary" />
                        ) : isActive ? (
                          <div className="relative w-6 h-6">
                            <div className="absolute inset-0 rounded-full border-2 border-primary/30"></div>
                            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-primary animate-spin"></div>
                          </div>
                        ) : (
                          <CheckIcon className="text-neutral-300" />
                        )}
                      </div>
                      <span
                        className={cn(
                          "text-base leading-relaxed transition-colors duration-300",
                          isActive ? "text-neutral-800 font-medium" : "text-neutral-400"
                        )}
                      >
                        {message}
                      </span>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

